import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ProductSummary } from './types';

// Lightweight client-side cart. Holds the chef's draft order lines before
// submission (product id → quantity + pack format). Server state (catalog,
// orders) stays in React Query; this is deliberately local UI state.
export interface CartLine {
  productId: string;
  supplierId: string;
  name: string;
  unitOfMeasure: string;
  // Chosen pack format; required before a quantity can be set when the
  // product has formats, so chefs pick one instead of taking the first.
  formatId?: string;
  formatLabel?: string;
  quantity: number;
}

interface CartContextValue {
  lines: Map<string, CartLine>;
  setQuantity: (product: ProductSummary, quantity: number) => void;
  setFormat: (product: ProductSummary, formatId: string) => void;
  remove: (productId: string) => void;
  clear: () => void;
  count: number;
}

const CartContext = createContext<CartContextValue | null>(null);

function baseLine(product: ProductSummary, prev?: CartLine): CartLine {
  return {
    productId: product.id,
    supplierId: product.supplierId,
    name: product.name,
    unitOfMeasure: product.unitOfMeasure,
    formatId: prev?.formatId,
    formatLabel: prev?.formatLabel,
    quantity: prev?.quantity ?? 0,
  };
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [lines, setLines] = useState<Map<string, CartLine>>(new Map());

  const setQuantity = useCallback((product: ProductSummary, quantity: number) => {
    setLines((prev) => {
      const next = new Map(prev);
      const line = prev.get(product.id);
      if (quantity <= 0 && !line?.formatId) {
        next.delete(product.id);
      } else {
        next.set(product.id, { ...baseLine(product, line), quantity: Math.max(0, quantity) });
      }
      return next;
    });
  }, []);

  const setFormat = useCallback((product: ProductSummary, formatId: string) => {
    setLines((prev) => {
      const next = new Map(prev);
      const format = product.formats.find((f) => f.id === formatId);
      next.set(product.id, { ...baseLine(product, prev.get(product.id)), formatId, formatLabel: format?.label });
      return next;
    });
  }, []);

  const remove = useCallback((productId: string) => {
    setLines((prev) => {
      const next = new Map(prev);
      next.delete(productId);
      return next;
    });
  }, []);

  const clear = useCallback(() => setLines(new Map()), []);

  const value = useMemo<CartContextValue>(
    () => ({
      lines,
      setQuantity,
      setFormat,
      remove,
      clear,
      count: [...lines.values()].filter((l) => l.quantity > 0).length,
    }),
    [lines, setQuantity, setFormat, remove, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
